import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import InstallAppButton from "../components/InstallAppButton";

const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";

const STATUS_LABEL = {
  trialing: "Free trial",
  active: "Active",
  past_due: "Payment failed — please update your card",
  canceled: "Canceled",
  manual: "Active",
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => { api.get("/billing/connect/status").then((r) => setConnectStatus(r.data)); }, []);

  const openConnectOnboarding = async () => {
    setConnectBusy(true);
    try {
      const { data } = await api.post("/billing/connect/onboard");
      window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setConnectBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        labour_rate_per_hour: parseFloat(settings.labour_rate_per_hour) || 0,
        default_margin_pct: parseFloat(settings.default_margin_pct) || 0,
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPortalBusy(false);
    }
  };

  return (
    <div className="space-y-8 animate-rise" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Account</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Settings</h1>
      </div>

      <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-4" data-testid="settings-plan-section">
        <h2 className="font-heading font-semibold text-xl">Your plan</h2>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm text-ink/60">
              {user?.tier ? `${user.tier === "pro" ? "Pro" : "Basic"} plan` : "No plan yet"}
              {user?.subscription_status && ` · ${STATUS_LABEL[user.subscription_status] || user.subscription_status}`}
            </p>
            {user?.trial_ends_at && user?.subscription_status === "trialing" && (
              <p className="text-xs text-ink/40 mt-1">Trial ends {new Date(user.trial_ends_at).toLocaleDateString("en-GB")}</p>
            )}
            {user?.current_period_end && user?.subscription_status !== "trialing" && (
              <p className="text-xs text-ink/40 mt-1">Renews {new Date(user.current_period_end).toLocaleDateString("en-GB")}</p>
            )}
          </div>
          <div className="flex gap-2">
            {user?.tier !== "pro" && (
              <a href="/pricing" data-testid="settings-upgrade-link"
                className="h-10 px-4 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium flex items-center transition-colors duration-200">
                Upgrade to Pro
              </a>
            )}
            {user?.stripe_customer_id && (
              <button onClick={openPortal} disabled={portalBusy} data-testid="settings-manage-billing"
                className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-sand/40 text-sm font-medium transition-colors duration-200 disabled:opacity-60">
                {portalBusy ? "Redirecting…" : "Manage billing"}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-3" data-testid="settings-payments-section">
        <h2 className="font-heading font-semibold text-xl">Payments</h2>
        <p className="text-sm text-ink/60 max-w-md">
          Connect Stripe to send invoices and get paid by your own clients · card, Klarna and Clearpay all land straight in your own bank account.
        </p>
        {connectStatus?.charges_enabled ? (
          <p className="text-sm text-tea-text font-medium" data-testid="settings-connect-status">Stripe connected &middot; ready to send invoices</p>
        ) : (
          <button onClick={openConnectOnboarding} disabled={connectBusy} data-testid="settings-connect-button"
            className="h-10 px-4 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium transition-colors duration-200 disabled:opacity-60">
            {connectBusy ? "Redirecting…" : connectStatus?.connected ? "Finish connecting Stripe" : "Connect with Stripe"}
          </button>
        )}
      </section>

      <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-3" data-testid="settings-install-section">
        <h2 className="font-heading font-semibold text-xl">Install Mnandi</h2>
        <p className="text-sm text-ink/60 max-w-md">Add Mnandi to your home screen for one-tap access, no browser bar.</p>
        <InstallAppButton />
      </section>

      {settings && (
        <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-4" data-testid="settings-costing-section">
          <h2 className="font-heading font-semibold text-xl">Costing defaults</h2>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Labour rate £/hr</label>
              <input type="number" step="0.5" min="0" data-testid="settings-labour-rate-input" className={`${inputCls} mt-1 font-mono-num`}
                value={settings.labour_rate_per_hour}
                onChange={(e) => setSettings({ ...settings, labour_rate_per_hour: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Default margin %</label>
              <input type="number" step="1" min="0" max="95" data-testid="settings-margin-input" className={`${inputCls} mt-1 font-mono-num`}
                value={settings.default_margin_pct}
                onChange={(e) => setSettings({ ...settings, default_margin_pct: e.target.value })} />
            </div>
          </div>
          <button onClick={saveSettings} disabled={busy} data-testid="settings-save-button"
            className="h-11 px-5 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium transition-colors duration-200 disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </section>
      )}
      <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-3" data-testid="settings-legal-section">
        <h2 className="font-heading font-semibold text-xl">Legal &amp; support</h2>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink/60">
          <a href="/terms" className="hover:text-ink underline">Terms of Service</a>
          <a href="/privacy" className="hover:text-ink underline">Privacy Policy</a>
          <a href="mailto:hello@mnandi.app" className="hover:text-ink underline">hello@mnandi.app</a>
        </div>
      </section>
    </div>
  );
}
